<template>
  <div class="fixed top-4 right-4 z-50 w-80 space-y-2">
    <transition-group 
      name="toast"
      tag="div"
      class="space-y-2"
    >
      <div 
        v-for="notification in notifications" 
        :key="notification.id"
        :class="[
          'rounded-md p-4 shadow-md',
          getTypeClass(notification.type)
        ]"
      >
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <component :is="getIcon(notification.type)" class="h-5 w-5" aria-hidden="true" />
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm">
              {{ notification.message }}
            </p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button
              type="button"
              @click="dismissNotification(notification.id)"
              class="inline-flex text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
              :class="getCloseButtonClass(notification.type)"
            >
              <span class="sr-only">Close</span>
              <XMarkIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <!-- Progress bar -->
        <div 
          v-if="notification.timeout !== 0"
          class="mt-2 h-1 rounded overflow-hidden bg-gray-200"
        >
          <div 
            :class="getProgressBarClass(notification.type)"
            class="h-full transition-all duration-100"
            :style="{ width: getProgressWidth(notification) + '%' }"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useToast } from '@/composables/toast'
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  XCircleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

const toast = useToast()
const notifications = computed(() => toast.notifications.value)

// Track notification timeouts
const notificationTimeouts = ref(new Map())

// Dismiss a notification
const dismissNotification = (id) => {
  // Clear any existing timeout
  if (notificationTimeouts.value.has(id)) {
    clearTimeout(notificationTimeouts.value.get(id))
    notificationTimeouts.value.delete(id)
  }

  // Remove the notification
  toast.remove(id)
}

const getTypeClass = (type) => {
  switch (type) {
    case 'success':
      return 'bg-green-50 text-green-800'
    case 'error':
      return 'bg-red-50 text-red-800'
    case 'warning':
      return 'bg-yellow-50 text-yellow-800'
    case 'info':
    default:
      return 'bg-blue-50 text-blue-800'
  }
}

const getCloseButtonClass = (type) => {
  switch (type) {
    case 'success':
      return 'focus:ring-green-500 text-green-500 hover:bg-green-100'
    case 'error':
      return 'focus:ring-red-500 text-red-500 hover:bg-red-100'
    case 'warning':
      return 'focus:ring-yellow-500 text-yellow-500 hover:bg-yellow-100'
    case 'info':
    default:
      return 'focus:ring-blue-500 text-blue-500 hover:bg-blue-100'
  }
}

const getProgressBarClass = (type) => {
  switch (type) {
    case 'success':
      return 'bg-green-500'
    case 'error':
      return 'bg-red-500'
    case 'warning':
      return 'bg-yellow-500'
    case 'info':
    default:
      return 'bg-blue-500'
  }
}

const getProgressWidth = (notification) => {
  // Default timeout is 5000ms
  const timeout = notification.timeout || 5000
  const elapsed = Date.now() - notification.timestamp
  const remaining = Math.max(0, timeout - elapsed)
  return (remaining / timeout) * 100
}

const getIcon = (type) => {
  switch (type) {
    case 'success':
      return CheckCircleIcon
    case 'error':
      return XCircleIcon
    case 'warning':
      return ExclamationTriangleIcon
    case 'info':
    default:
      return InformationCircleIcon
  }
}
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
